import Box from '@mui/material/Box';
import { type Editor, EditorContent } from '@tiptap/react';
import { CustomStyles } from '@tyro/core/editor';
import { useReaderActions } from '../../store/documentation-reader-provider';
import { parseInternalDocLink } from '../../utils/internal-link';

const proseMirrorStyles = {
  outline: 'none',
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    scrollMarginTop: '80px',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
  },
  '& pre, & code': {
    maxWidth: '100%',
    overflowX: 'auto',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  '& iframe, & video': { maxWidth: '100%' },
  // Match the Tyro MuiLink convention (underline on hover only).
  '& a': {
    color: 'primary.main',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  '& a[data-link-state="broken"]': {
    color: 'error.main',
    textDecorationColor: 'inherit',
    '&::after': {
      content: '"⚠"',
      marginLeft: '0.25em',
      fontSize: '0.85em',
    },
  },
  '& a[data-link-state="pending"]': {
    color: 'warning.main',
    textDecorationColor: 'inherit',
    '&::after': {
      content: '"⏱"',
      marginLeft: '0.25em',
      fontSize: '0.85em',
    },
  },
} as const;

interface ReaderProseBodyProps {
  editor: Editor | null;
  // True when the parent rendered a video embed above; adds top margin.
  hasVideoAbove?: boolean;
  spaceSlug: string;
  pageSlug: string;
  onOpenInEditor?: (spaceSlug: string, pageSlug: string) => void;
}

export function ReaderProseBody({
  editor,
  hasVideoAbove = false,
  spaceSlug,
  pageSlug,
  onOpenInEditor,
}: ReaderProseBodyProps) {
  const { navigateToPage } = useReaderActions();
  const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement).closest('a');
    if (!anchor) return;
    const linkState = anchor.dataset.linkState;
    if (linkState === 'broken' || linkState === 'pending') {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      onOpenInEditor?.(spaceSlug, pageSlug);
      return;
    }
    const href = anchor.getAttribute('href') ?? '';
    const internal = parseInternalDocLink(href);
    if (!internal) return;
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    navigateToPage(internal.spaceSlug, internal.pageSlug);
  };

  return (
    <Box
      onClickCapture={handleClickCapture}
      sx={{
        ...CustomStyles,
        overflowX: 'hidden',
        mt: hasVideoAbove ? 3 : 0,
        '& .ProseMirror': proseMirrorStyles,
      }}
    >
      <EditorContent editor={editor} />
    </Box>
  );
}
